import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import ConfirmDialog from "../components/ConfirmDialog";
import ProviderModal from "../components/ProviderModal";
import Toast from "../components/Toast";
import useProviders from "../hooks/useProviders";
import { useScrollRestoration, useValidOption } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { fetchBranches } from "../services/branchService";
import { deleteProvider } from "../services/providerService";
import { fetchCatalogue } from "../services/serviceService";
import { labelFor } from "../services/enumLabels";

/**
 * BIZ-07 — the people who perform services.
 *
 * Not the same list as Staff, which is portal logins. A provider may have a
 * login and usually does not; the two screens answer different questions.
 */
export default function Providers() {
  const { t, i18n } = useTranslation();

  const {
    providers,
    meta,
    page,
    setPage,
    filters,
    applyFilters,
    resetFilters,
    searchTerm,
    setSearchTerm,
    submitSearch,
    loading,
    error,
    reload,
  } = useProviders();

  // Back from a detail page lands where the user was.
  useScrollRestoration(!loading);

  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  // A stale id in the URL (deleted, or not this user's) falls back to "any".
  useValidOption(filters.branch, branches.length ? branches : null, () => applyFilters({ branch: "" }));
  useValidOption(filters.service, services.length ? services : null, () => applyFilters({ service: "" }));

  // The branch picker and the branch filter both need every branch.
  const loadBranches = useCallback(async () => {
    try {
      const { branches: list } = await fetchBranches(1, 100);
      setBranches(list);
    } catch {
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches, i18n.language]);

  // The eligibility picker reads the business's catalogue.
  useEffect(() => {
    // The whole catalogue feeds a picker, so ask for a page big enough to hold it.
    fetchCatalogue({ perPage: 100 })
      .then(({ services: list }) => setServices(list))
      .catch(() => setServices([]));
  }, [i18n.language]);

  const filtersApplied = filters.search || filters.branch || filters.service || filters.employmentStatus;

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteProvider(deleting.id);
      setToast({
        type: "success",
        message: t("providers.deleted", { name: deleting.display_name }),
      });
      setDeleting(null);
      reload();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("providers.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  /** active / leave / inactive each get their own colour. */
  const statusBadge = (status) => {
    const tones = {
      active: "bg-accent-wash text-accent-text",
      leave: "bg-warning-wash text-warning-text",
      inactive: "bg-raised text-ink-2",
    };

    return (
      <span className={`rounded-full px-3 py-1 text-xs font-medium ${tones[status] ?? tones.inactive}`}>
        {labelFor("employment_status", status)}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("providers.title")}</h1>
          <p className="text-sm text-ink-2">
            {meta ? t("providers.count", { count: meta.total }) : t("providers.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="btn-primary"
        >
          {t("providers.new")}
        </button>
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
        className="card mb-4 flex flex-wrap items-end gap-3 p-4"
      >
        <div className="min-w-[200px] flex-1">
          <label className="field-label" htmlFor="provider-search">
            {t("providers.filters.search")}
          </label>
          <input
            id="provider-search"
            className="field-input"
            placeholder={t("providers.filters.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="provider-branch-filter">
            {t("providers.filters.branch")}
          </label>
          <select
            id="provider-branch-filter"
            className="field-input"
            value={filters.branch}
            onChange={(e) => applyFilters({ branch: e.target.value })}
          >
            <option value="">{t("providers.filters.any")}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="provider-service-filter">
            {t("providers.filters.service")}
          </label>
          <select
            id="provider-service-filter"
            className="field-input"
            value={filters.service}
            onChange={(e) => applyFilters({ service: e.target.value })}
          >
            <option value="">{t("providers.filters.any")}</option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>
                {service.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="provider-status-filter">
            {t("providers.filters.status")}
          </label>
          <select
            id="provider-status-filter"
            className="field-input"
            value={filters.employmentStatus}
            onChange={(e) => applyFilters({ employmentStatus: e.target.value })}
          >
            <option value="">{t("providers.filters.any")}</option>
            <option value="active">{t("providers.status.active")}</option>
            <option value="leave">{t("providers.status.leave")}</option>
            <option value="inactive">{t("providers.status.inactive")}</option>
          </select>
        </div>

        <div className="flex gap-2">
          <button type="submit" className="btn-primary">
            {t("providers.filters.apply")}
          </button>
          {filtersApplied && (
            <button
              type="button"
              onClick={() => {
                resetFilters();
              }}
              className="btn-secondary"
            >
              {t("providers.filters.clear")}
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : providers.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">
            {filtersApplied ? t("providers.noMatches") : t("providers.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("providers.table.name")}</th>
                  <th className="px-4 py-3">{t("providers.table.branches")}</th>
                  <th className="px-4 py-3">{t("providers.table.services")}</th>
                  <th className="px-4 py-3">{t("providers.table.online")}</th>
                  <th className="px-4 py-3">{t("providers.table.status")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => (
                  <tr key={provider.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {provider.photo_url ? (
                          <img
                            src={provider.photo_url}
                            alt=""
                            className="h-9 w-9 rounded-full object-cover"
                          />
                        ) : (
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-raised text-xs font-medium text-ink-2">
                            {(provider.display_name ?? "?").slice(0, 1)}
                          </span>
                        )}
                        <div>
                          <Link
                            to={`/providers/${provider.id}`}
                            className="font-medium text-accent-text hover:underline"
                          >
                            {provider.display_name}
                          </Link>
                          {provider.job_title && (
                            <p className="text-xs text-ink-2">{provider.job_title}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {provider.branches?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {provider.branches.map((branch) => (
                            <span
                              key={branch.id}
                              className="rounded-full bg-raised px-2 py-1 text-xs text-ink"
                            >
                              {branch.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-ink-2">{t("providers.noBranches")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {t("providers.serviceCount", { count: provider.services_count ?? 0 })}
                    </td>
                    <td className="px-4 py-3">
                      {provider.is_bookable_online ? (
                        <span className="text-xs text-accent-text">{t("common.yes")}</span>
                      ) : (
                        <span className="text-xs text-ink-2">{t("common.no")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{statusBadge(provider.employment_status)}</td>
                    <td className="px-4 py-3 text-end">
                      <div className="flex justify-end gap-3">
                        <Link
                          to={`/providers/${provider.id}`}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("common.view")}
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(provider);
                            setModalOpen(true);
                          }}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteError("");
                            setDeleting(provider);
                          }}
                          className="text-sm font-medium text-danger-text hover:underline"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      <ProviderModal
        provider={editing}
        branches={branches}
        services={services}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(saved, wasEditing) => {
          setToast({
            type: "success",
            message: wasEditing
              ? t("providers.updated", { name: saved.display_name })
              : t("providers.created", { name: saved.display_name }),
          });
          reload();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("providers.deleteTitle")}
        message={t("providers.deleteMessage", { name: deleting?.display_name ?? "" })}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
